import * as React from 'react'
import { BusinessTripModel } from '@/app/models/businessTripModel'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'

import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { useTable } from '@/modules/drivers/context/TableContext'
import { getBusinessTripsColumns } from '@/modules/drivers/constants/tables'
import BusinessTripRow from './BusinessTripRow'
import BusinessTripContextMenu, {
  BusinessTripMenuTarget,
} from './BusinessTripContextMenu'
import BusinessTripContextMenuDialogs from './BusinessTripContextMenuDialogs'
import useBusinessTripContextMenuActions from '../../hooks/useBusinessTripContextMenuActions'
import { getRolesInCompany } from '@/app/services/local/roleService'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { FaIcon } from '@/lib/fa'
import { faSuitcase } from '@fortawesome/free-solid-svg-icons'

interface BusinessTripsTableProps {
  trips: BusinessTripModel[]
  companyId: string
  selectedDriverId: string
  onRefresh?: () => void
  /** Kolumny dat utworzenia / modyfikacji (przełączane z paska). */
  showAuditDates?: boolean
}

const BusinessTripsTable: React.FC<BusinessTripsTableProps> = ({
  trips,
  companyId,
  selectedDriverId,
  onRefresh,
  showAuditDates = false,
}) => {
  const { t } = useLocalize()
  const { toggleAll, toggleId, selectedGroup, isSelectedAny, setAllIds } =
    useTable()

  const rolesInCompany = getRolesInCompany(companyId) || []
  const canEditDelegation = rolesInCompany.some((r) => r.canEditDelegations)
  const canViewDelegation = rolesInCompany.some((r) => r.canViewDelegations)
  const enableEdit = canEditDelegation || canViewDelegation

  const columns = React.useMemo(
    () => getBusinessTripsColumns(t, { includeAuditDates: showAuditDates }),
    [t, showAuditDates],
  )
  const { sortedData, sortConfig, handleHeaderClick } = useSortableData({
    data: trips,
    tableColumns: columns,
  })

  React.useEffect(() => {
    if (trips.length > 0) {
      setAllIds(
        trips.map((trip) => ({
          id: typeof trip.id === 'string' ? parseInt(trip.id, 10) : trip.id,
        })),
      )
    }
  }, [trips, setAllIds])

  const selectedIds = selectedGroup?.ids || []

  const [menuTarget, setMenuTarget] =
    React.useState<BusinessTripMenuTarget | null>(null)

  const {
    dialogState,
    handleAction,
    closeDialog,
    handleConfirmCountry,
    handleConfirmSettlementMonth,
    handleConfirmSettlementDate,
    handleConfirmExchangeRateDate,
    handleConfirmVehicle,
  } = useBusinessTripContextMenuActions({
    companyId,
    driverId: selectedDriverId,
    selectedIds,
    allTrips: trips,
    onRefresh: onRefresh || (() => {}),
  })

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col ">
      <div
        className={`tw-min-h-0 tw-flex-1 tw-overflow-x-auto tw-overflow-y-auto tw-rounded-lg tw-border-x-base-300 tw-border-t-base-300 ${
          isSelectedAny && 'tw-rounded-t-none'
        }`}
      >
        {sortedData.length > 0 ? (
          <table className="tw-w-full tw-table-auto tw-border-collapse !tw-rounded-3xl tw-pt-2">
            <TableHeader
              columns={columns}
              fontSize="md"
              isSelectedAny={isSelectedAny}
              rounded
              sortConfig={sortConfig}
              bolder
              disabledToggle={!enableEdit}
              toggleAll={toggleAll}
              onHeaderClick={handleHeaderClick}
            />
            <tbody>
              {sortedData.map((trip) => (
                <BusinessTripRow
                  key={trip.id}
                  trip={trip}
                  companyId={companyId}
                  selectedDriverId={selectedDriverId}
                  selectedIds={selectedIds}
                  toggleId={toggleId}
                  isSelectedAny={isSelectedAny}
                  showAuditDates={showAuditDates}
                  onContextMenu={(event, contextTrip) => {
                    event.preventDefault()
                    setMenuTarget({
                      trip: contextTrip,
                      anchorPoint: { x: event.clientX, y: event.clientY },
                    })
                  }}
                />
              ))}
            </tbody>
          </table>
        ) : (
          <div className="tw-flex-1  tw-h-screen tw-max-h-[700px] tw-flex tw-justify-center tw-items-center tw-flex-col tw-gap-5">
            <FaIcon icon={faSuitcase} className='tw-text-[#D1E2F6] tw-w-10 tw-h-10'/>
            <div className='tw-flex tw-flex-col tw-items-center'>
              <p className='tw-text-content-base tw-text-lg tw-font-semibold'>Brak delegacji</p>
              <p className='tw-text-content-base tw-text-xs tw-opacity-80'>Brak delegacji dla wskazanego zakresu dat</p>
            </div>
          </div>
        )}
      </div>

      <BusinessTripContextMenu
          target={menuTarget}
          onClose={() => setMenuTarget(null)}
          onAction={handleAction}
        />

      <BusinessTripContextMenuDialogs
        dialogState={dialogState}
        companyId={companyId}
        exchangeRateDate={(dialogState.trip as any)?.exchangeRateDate ?? ''}
        onClose={closeDialog}
        onConfirmCountry={handleConfirmCountry}
        onConfirmSettlementMonth={handleConfirmSettlementMonth}
        onConfirmSettlementDate={handleConfirmSettlementDate}
        onConfirmExchangeRateDate={handleConfirmExchangeRateDate}
        onConfirmVehicle={handleConfirmVehicle}
      />
    </div>
  )
}

export default BusinessTripsTable
